import { Injectable } from '@angular/core';
import { BehaviorSubject, concat, Observable, of } from 'rxjs';
import { catchError, filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { HttpInternalService } from './http.service';

/**
 * SD-1248: advisor practices. Typed calls to every in-app `api/practice/*` route.
 *
 * The routes and shapes are docs/SD-1248-api-contract.md in Synced-Backend, word for word. Every
 * route that names the practice takes it as `organisationId`, so the server's tenant filter checks
 * the caller belongs to the practice organisation; a client's organisation is never sent as the
 * practice and no list of clients is ever sent for the server to trust.
 */

export type PracticeRole = 'manager' | 'advisor';
export type Ledger = 'xero' | 'qbo' | 'none';
export type ClientStatus = 'setting_up' | 'connection_lost' | 'action_required' | 'agent_working' | 'up_to_date';
export type InvitationKind = 'client' | 'member' | 'owner';
export type InvitationStatus = 'pending' | 'accepted' | 'declined' | 'expired' | 'cancelled';

/** One practice the signed-in user belongs to. */
export interface PracticeMembership {
  organisationId: string;
  name: string;
  role: PracticeRole;
  clientCount: number;
  needsActionCount: number;
  /** The practice's own logo; null shows initials. */
  logoUrl?: string | null;
}

export interface PracticeMe {
  practices: PracticeMembership[];
}

export interface ClientConnection {
  kind: 'accounting' | 'bankfeed';
  name: string;
  ok: boolean;
  detail: string | null;
}

/**
 * One row of the All clients page. A null figure means the user may not see it in that client
 * (or it is not computed yet) and is shown as "—", never as 0.
 */
export interface ClientSummary {
  organisationId: string;
  name: string;
  /** SD-1252: the team member who looks after the client; their rule-made tasks go to them. */
  leadUserId?: string | null;
  leadName?: string | null;
  /** The manager's logo, else the one found for the client's website; null shows initials. */
  logoUrl: string | null;
  /** From the client's profile: null until a manager gives a website. */
  website?: string | null;
  /** The manager's own words, else the description found for the website. */
  description?: string | null;
  industry?: string | null;
  location?: string | null;
  ledger: Ledger;
  /** The last successful ledger refresh. */
  lastSyncUtc: string | null;
  /** The latest ledger refresh, worked or not; null when none was ever logged. */
  lastSyncAttemptUtc?: string | null;
  /** What the latest refresh was of, when it failed ("Contacts"); null when it worked. */
  lastSyncFailedTable?: string | null;
  status: ClientStatus;
  expensesDetailsNeeded: number | null;
  expensesForReview: number | null;
  statementsActionRequired: number | null;
  statementsAwaitingApproval: number | null;
  billsOverdueAmount: number | null;
  /** Bank transactions not reconciled or matched; null when the user cannot see every account. */
  transactionsToReconcile: number | null;
  /** Everything in the Inbox's For Review: expensesDetailsNeeded + expensesForReview. */
  docsForReview: number | null;
  agentRunsInProgress: number;
  connections: ClientConnection[];
  addedAtUtc: string;
}

export interface PracticeClients {
  practiceOrganisationId: string;
  practiceName: string;
  role: PracticeRole;
  generatedAtUtc: string;
  clients: ClientSummary[];
}

export type LookupStatus = 'found' | 'not_found' | 'failed' | 'unavailable';
export type LogoSource = 'custom' | 'apollo' | 'favicon';

/** What looking a client's website up found (Apollo, then the site's icon for the logo). */
export interface OrganisationLookup {
  domain: string;
  status: LookupStatus;
  name: string | null;
  logoUrl: string | null;
  logoSource: LogoSource | null;
  description: string | null;
  industry: string | null;
  employees: number | null;
  location: string | null;
  linkedinUrl: string | null;
  lookedUpAtUtc: string | null;
}

/**
 * A client's logo and details. A manager gives the website and the rest is looked up; their own
 * logo or description, when given, wins over what was found.
 */
export interface ClientProfile {
  organisationId: string;
  website: string | null;
  domain: string | null;
  /** What the page shows: the manager's description, else the found one. */
  description: string | null;
  customDescription: boolean;
  /** What the page shows: the manager's logo, else the found one. */
  logoUrl: string | null;
  logoSource: LogoSource | null;
  found: OrganisationLookup | null;
  lookupStatus: LookupStatus | null;
}

export interface PracticeMember {
  userId: string;
  name: string;
  email: string;
  practiceRole: PracticeRole;
  allClients: boolean;
  clientOrganisationIds: string[];
  isActive: boolean;
}

export interface PracticeInvitation {
  requestId: string;
  kind: InvitationKind;
  email: string;
  status: InvitationStatus;
  message: string | null;
  requestedByName: string;
  createdAtUtc: string;
  expiresAtUtc: string;
  clientOrganisationId: string | null;
}

export interface LinkableOrganisation {
  organisationId: string;
  name: string;
  ledger: Ledger;
  managedElsewhere: boolean;
}

export interface LinkResult {
  linked: string[];
  refused: { organisationId: string; reason: 'not_admin' | 'already_managed' | 'is_practice' }[];
}

/** No accounting platform: a new client has none until it is connected from its Integrations page. */
export interface NewClientRequest {
  name: string;
  ownerEmail?: string;
}

export interface NewClientResult {
  clientOrganisationId: string;
  ownerInvitation: PracticeInvitation | null;
}

export interface MemberCandidate {
  userId: string;
  name: string;
  email: string;
}

export interface AddMemberRequest {
  userId?: string;
  email?: string;
  practiceRole: PracticeRole;
  allClients: boolean;
  clientOrganisationIds?: string[];
}

/** POST members answers 201 `{ member }` for an existing person, 202 `{ invitation }` by email. */
export interface AddMemberResult {
  member?: PracticeMember;
  invitation?: PracticeInvitation;
}

/** What an invitation link asks of the signed-in user. */
export interface InvitationDetail {
  kind: InvitationKind;
  practiceName: string;
  inviterName: string;
  message: string | null;
  status: InvitationStatus;
  expiresAtUtc: string;
  email: string;
  emailMatches: boolean;
  practiceRole?: PracticeRole;
  clientName?: string;
  /** Kind client only: the organisations the signed-in invitee administers. */
  organisations: { organisationId: string; name: string; managedElsewhere: boolean }[];
}

/** An invitation as /practice-join shows it to someone not signed in. */
export interface InvitationPreview {
  kind: InvitationKind;
  practiceName: string;
  inviterName: string;
  message: string | null;
  status: InvitationStatus;
  expiresAtUtc: string;
  email: string;
  practiceRole?: PracticeRole;
  clientName?: string;
  /** The invited address already has a Synced account: sign in rather than create one. */
  hasAccount: boolean;
}

export interface InvitationAcceptResult {
  kind: InvitationKind;
  practiceName: string;
  practiceOrganisationId: string;
  clientOrganisationId?: string;
}

export interface ManagedBy {
  /** logoUrl: the practice's logo for the client's people, or null. */
  practice: { organisationId: string; name: string; sinceUtc: string; addedByName: string; logoUrl?: string | null } | null;
  canRemove: boolean;
  practiceUserIds: string[];
}

/** The `{ error, message }` body the practice API refuses with. */
export interface PracticeApiError {
  status: number;
  error: string | null;
  message: string | null;
}

/** Reads the contract's error body out of an HttpErrorResponse (or anything else thrown). */
export function practiceError(err: any): PracticeApiError {
  const body = err && err.error && typeof err.error === 'object' ? err.error : {};
  return {
    status: err && typeof err.status === 'number' ? err.status : 0,
    error: body.error || null,
    message: body.message || null
  };
}

const EMPTY_ME: PracticeMe = { practices: [] };

@Injectable({ providedIn: 'root' })
export class PracticeService {

  private _refresh$ = new BehaviorSubject<number>(0);

  /**
   * The practices the signed-in user belongs to; `{ practices: [] }` for everyone else, and on any
   * failure (a practice-less menu is the safe answer). Cached; call refresh() after sign-in and
   * after anything that changes a practice or its clients.
   */
  readonly me$: Observable<PracticeMe> = this._refresh$.pipe(
    switchMap(() => this.http.getRequest<PracticeMe>('practice/me').pipe(
      map(res => ({ practices: ((res && res.practices) || []).map(p => this.withLogo({ ...p, logoUrl: p.logoUrl || null })) })),
      catchError(() => of(EMPTY_ME))
    )),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  /** True when the user belongs to at least one practice. */
  readonly hasPractice$: Observable<boolean> = this.me$.pipe(map(me => me.practices.length > 0));

  constructor(private http: HttpInternalService) { }

  refresh(): void {
    this._refresh$.next(this._refresh$.value + 1);
  }

  /**
   * Refreshes and resolves with the fresh answer. me$ replays the cached answer to anyone who
   * subscribes before the new one arrives, so code that is about to depend on a change (going to
   * /clients just after joining a practice: PracticeGuard reads me$) waits for this instead.
   */
  reload(): Promise<PracticeMe> {
    let previous: PracticeMe | undefined;
    this.me$.pipe(take(1)).subscribe(me => previous = me).unsubscribe();
    const fresh = this.me$.pipe(filter(me => me !== previous), take(1)).toPromise();
    this.refresh();
    return fresh;
  }

  /**
   * The logo of each organisation the user belongs to, by id, for the organisation menu. Emits the
   * copy kept from the last visit straight away, so the menu draws its logos without waiting, then
   * the fresh answer (which it keeps for next time). The images themselves are cached by the browser:
   * each address carries its image's version.
   */
  organisationLogos(): Observable<{ [organisationId: string]: string }> {
    const key = 'OrganisationLogos:' + (localStorage.getItem('Id') || '');
    let kept: { [organisationId: string]: string } | null = null;
    try { kept = JSON.parse(localStorage.getItem(key) || 'null'); } catch { }
    const fresh = this.http.getRequest<{ [organisationId: string]: string }>('practice/organisation-logos').pipe(
      map(res => {
        const logos: { [organisationId: string]: string } = {};
        Object.keys(res || {}).forEach(id => logos[id.toLowerCase()] = this.http.apiUrl(res[id]));
        try { localStorage.setItem(key, JSON.stringify(logos)); } catch { }
        return logos;
      }),
      catchError(() => of(null)),
      filter(logos => logos !== null));
    return kept ? concat(of(kept), fresh) : fresh;
  }

  // ── Reading ──────────────────────────────────────────────────────────────────────────────

  /** SD-1252: who looks after a client (one of the practice's people), or null for nobody. */
  setClientLead(practiceId: string, clientOrganisationId: string, leadUserId: string | null): Observable<{ organisationId: string; leadUserId: string | null; leadName: string | null }> {
    return this.http.putRequest<{ organisationId: string; leadUserId: string | null; leadName: string | null }>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}/lead`, { organisationId: practiceId, leadUserId: leadUserId || null });
  }

  clients(practiceId: string): Observable<PracticeClients> {
    return this.http.getRequest<PracticeClients>('practice/clients', { organisationId: practiceId }).pipe(
      map(res => res && { ...res, clients: (res.clients || []).map(c => this.withLogo(c)) }));
  }

  client(practiceId: string, clientOrganisationId: string): Observable<ClientSummary> {
    return this.http.getRequest<ClientSummary>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}/summary`, { organisationId: practiceId })
      .pipe(map(c => this.withLogo(c)));
  }

  /** An uploaded logo is "/api/practice/clients/…/logo": point it at the API host, not the page's. */
  private withLogo<T extends { logoUrl: string | null }>(item: T): T {
    return item && item.logoUrl ? { ...item, logoUrl: this.http.apiUrl(item.logoUrl) } : item;
  }

  // ── The practice's own logo (managers) ───────────────────────────────────────────────────

  uploadPracticeLogo(practiceId: string, file: File): Observable<{ logoUrl: string | null }> {
    const form = new FormData();
    form.append('file', file);
    return this.http.postClearRequest<{ logoUrl: string | null }>(
      `practice/logo?organisationId=${encodeURIComponent(practiceId)}`, form)
      .pipe(map(r => this.withLogo({ logoUrl: (r && r.logoUrl) || null })));
  }

  clearPracticeLogo(practiceId: string): Observable<{ logoUrl: string | null }> {
    return this.http.deleteRequest<{ logoUrl: string | null }>('practice/logo', { organisationId: practiceId });
  }

  // ── A client's logo and details (managers) ───────────────────────────────────────────────

  profile(practiceId: string, clientOrganisationId: string): Observable<ClientProfile> {
    return this.http.getRequest<ClientProfile>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}/profile`, { organisationId: practiceId })
      .pipe(map(p => this.withLogo(p)));
  }

  /** What the website gives, without saving anything. */
  lookupProfile(practiceId: string, clientOrganisationId: string, website: string): Observable<OrganisationLookup> {
    return this.http.postRequest<OrganisationLookup>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}/profile/lookup`, { organisationId: practiceId, website });
  }

  /** A blank website removes it; a blank description uses the one found for the website. */
  saveProfile(practiceId: string, clientOrganisationId: string, website: string, description: string): Observable<ClientProfile> {
    return this.http.putRequest<ClientProfile>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}/profile`,
      { organisationId: practiceId, website: website || '', description: description || '' })
      .pipe(map(p => this.withLogo(p)));
  }

  /** Multipart, so it goes without HttpInternalService's JSON content type. */
  uploadLogo(practiceId: string, clientOrganisationId: string, file: File): Observable<ClientProfile> {
    const form = new FormData();
    form.append('file', file);
    return this.http.postClearRequest<ClientProfile>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}/profile/logo?organisationId=${encodeURIComponent(practiceId)}`, form)
      .pipe(map(p => this.withLogo(p)));
  }

  clearLogo(practiceId: string, clientOrganisationId: string): Observable<ClientProfile> {
    return this.http.deleteRequest<ClientProfile>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}/profile/logo`, { organisationId: practiceId })
      .pipe(map(p => this.withLogo(p)));
  }

  refreshClients(practiceId: string): Observable<void> {
    return this.http.postRequest<void>('practice/clients/refresh', { organisationId: practiceId });
  }

  // ── Adding and removing clients ──────────────────────────────────────────────────────────

  createClient(practiceId: string, request: NewClientRequest): Observable<NewClientResult> {
    const body: any = { organisationId: practiceId, name: request.name };
    if (request.ownerEmail) {
      body.ownerEmail = request.ownerEmail;
    }
    return this.http.postRequest<NewClientResult>('practice/clients/new', body);
  }

  linkable(practiceId: string): Observable<LinkableOrganisation[]> {
    return this.http.getRequest<LinkableOrganisation[]>('practice/clients/linkable', { organisationId: practiceId });
  }

  link(practiceId: string, clientOrganisationIds: string[]): Observable<LinkResult> {
    return this.http.postRequest<LinkResult>('practice/clients/link',
      { organisationId: practiceId, clientOrganisationIds });
  }

  removeClient(practiceId: string, clientOrganisationId: string): Observable<void> {
    return this.http.deleteRequest<void>(
      `practice/clients/${encodeURIComponent(clientOrganisationId)}`, { organisationId: practiceId });
  }

  // ── Invitations the practice sent ────────────────────────────────────────────────────────

  invitations(practiceId: string): Observable<PracticeInvitation[]> {
    return this.http.getRequest<PracticeInvitation[]>('practice/client-requests', { organisationId: practiceId });
  }

  inviteClient(practiceId: string, email: string, message?: string): Observable<PracticeInvitation> {
    const body: any = { organisationId: practiceId, email };
    if (message) {
      body.message = message;
    }
    return this.http.postRequest<PracticeInvitation>('practice/client-requests', body);
  }

  resendInvitation(practiceId: string, requestId: string): Observable<PracticeInvitation> {
    return this.http.postRequest<PracticeInvitation>(
      `practice/client-requests/${encodeURIComponent(requestId)}/resend`, { organisationId: practiceId });
  }

  cancelInvitation(practiceId: string, requestId: string): Observable<void> {
    return this.http.deleteRequest<void>(
      `practice/client-requests/${encodeURIComponent(requestId)}`, { organisationId: practiceId });
  }

  // ── An invitation, from the invitee's side ───────────────────────────────────────────────

  invitation(token: string): Observable<InvitationDetail> {
    return this.http.getRequest<InvitationDetail>(`practice/invitations/${encodeURIComponent(token)}`);
  }

  /** Kind client sends the chosen organisation; member and owner send an empty body. */
  acceptInvitation(token: string, clientOrganisationId?: string): Observable<InvitationAcceptResult> {
    const body = clientOrganisationId ? { clientOrganisationId } : {};
    return this.http.postRequest<InvitationAcceptResult>(
      `practice/invitations/${encodeURIComponent(token)}/accept`, body);
  }

  declineInvitation(token: string): Observable<void> {
    return this.http.postRequest<void>(`practice/invitations/${encodeURIComponent(token)}/decline`, {});
  }

  /** Signed out: the invitation behind an email link. */
  previewInvitation(token: string): Observable<InvitationPreview> {
    return this.http.getRequest<InvitationPreview>(`practice-join/${encodeURIComponent(token)}`);
  }

  /**
   * Signed out, kinds member and owner: creates the invited address's account (no organisation)
   * and accepts the invitation. The caller then signs in with the same password.
   */
  joinWithNewAccount(token: string, firstName: string, lastName: string, password: string)
    : Observable<{ email: string; result: InvitationAcceptResult }> {
    return this.http.postRequest<{ email: string; result: InvitationAcceptResult }>(
      `practice-join/${encodeURIComponent(token)}/register`, { firstName, lastName, password });
  }

  // ── The client's side ────────────────────────────────────────────────────────────────────

  managedBy(clientOrganisationId: string): Observable<ManagedBy> {
    return this.http.getRequest<ManagedBy>('practice/managed-by', { organisationId: clientOrganisationId }).pipe(
      map(res => res && res.practice ? { ...res, practice: this.withLogo({ ...res.practice, logoUrl: res.practice.logoUrl || null }) } : res));
  }

  removeManagingPractice(clientOrganisationId: string): Observable<void> {
    return this.http.deleteRequest<void>('practice/managed-by', { organisationId: clientOrganisationId });
  }

  // ── The practice's people ────────────────────────────────────────────────────────────────

  members(practiceId: string): Observable<PracticeMember[]> {
    return this.http.getRequest<PracticeMember[]>('practice/members', { organisationId: practiceId });
  }

  memberCandidates(practiceId: string, q: string = ''): Observable<MemberCandidate[]> {
    return this.http.getRequest<MemberCandidate[]>('practice/members/candidates', { organisationId: practiceId, q });
  }

  addMember(practiceId: string, request: AddMemberRequest): Observable<AddMemberResult> {
    return this.http.postRequest<AddMemberResult>('practice/members', { organisationId: practiceId, ...request });
  }

  updateMember(practiceId: string, userId: string, practiceRole: PracticeRole, allClients: boolean): Observable<PracticeMember> {
    return this.http.putRequest<PracticeMember>(`practice/members/${encodeURIComponent(userId)}`,
      { organisationId: practiceId, practiceRole, allClients });
  }

  setMemberClients(practiceId: string, userId: string, clientOrganisationIds: string[]): Observable<PracticeMember> {
    return this.http.putRequest<PracticeMember>(`practice/members/${encodeURIComponent(userId)}/clients`,
      { organisationId: practiceId, clientOrganisationIds });
  }

  removeMember(practiceId: string, userId: string): Observable<void> {
    return this.http.deleteRequest<void>(`practice/members/${encodeURIComponent(userId)}`, { organisationId: practiceId });
  }
}
